/**
 * Item Permissions Tab
 * 
 * Configure filter rules for which items can be accessed.
 * Uses a JSON filter builder similar to the reference implementation.
 */

import { useState, useEffect } from 'react';
import { Info } from 'lucide-react';
import type { Permission } from '@/stores/access-control';
import { Textarea } from '@/components/ui/textarea';
import { FilterBuilder } from '@/components/filters';
import { useFields } from '@/hooks/useFields';

interface ItemPermissionsTabProps {
    permission: Permission;
    localPermission: Partial<Permission>;
    policyName: string;
    onUpdate: (updates: Partial<Permission>) => void;
}

// Dynamic variables
const DYNAMIC_VARIABLES = [
    { value: '$CURRENT_USER', label: 'Current User ID' },
    { value: '$CURRENT_ROLE', label: 'Current Role ID' },
    { value: '$NOW', label: 'Current Timestamp' },
];

export function ItemPermissionsTab({ permission, localPermission, policyName, onUpdate }: ItemPermissionsTabProps) {
    const [mode, setMode] = useState<'visual' | 'json'>('visual');
    const [jsonValue, setJsonValue] = useState(
        JSON.stringify(localPermission.permissions || {}, null, 2)
    );
    const [jsonError, setJsonError] = useState<string | null>(null);

    // Get fields for this collection
    const { fields } = useFields({ collection: permission.collection || null });

    // Update JSON when permissions change
    useEffect(() => {
        setJsonValue(JSON.stringify(localPermission.permissions || {}, null, 2));
    }, [localPermission.permissions]);

    const handleFilterChange = (filter: any) => {
        onUpdate({ permissions: filter });
    };

    const handleJsonSave = () => {
        try {
            const parsed = JSON.parse(jsonValue);
            setJsonError(null);
            onUpdate({ permissions: Object.keys(parsed).length > 0 ? parsed : null });
            setMode('visual');
        } catch {
            setJsonError('Invalid JSON format');
        }
    };

    return (
        <div className="space-y-6">
            {/* Notice */}
            <div className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4">
                <Info className="h-5 w-5 text-blue-600" />
                <div className="text-sm">
                    <p>
                        Configure filter rules to restrict which items the{' '}
                        <strong>{policyName || 'Public'}</strong> policy can{' '}
                        <strong>{permission.action}</strong> in the{' '}
                        <strong>{permission.collection}</strong> collection.
                    </p>
                    <p className="mt-2">
                        Leave empty to allow access to all items.
                    </p>
                </div>
            </div>

            {/* Toggle between visual and JSON */}
            <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-foreground">Filter Rules</h3>
                <button
                    type="button"
                    onClick={() => setMode(mode === 'visual' ? 'json' : 'visual')}
                    className="text-sm text-primary hover:underline"
                >
                    {mode === 'visual' ? 'Edit as JSON' : 'Use Visual Editor'}
                </button>
            </div>

            {mode === 'visual' ? (
                /* Visual Filter Builder */
                <div className="space-y-3">
                    <FilterBuilder
                        value={localPermission.permissions as any}
                        onChange={handleFilterChange}
                        fields={fields}
                        collection={permission.collection || null}
                    />
                </div>
            ) : (
                /* JSON Editor */
                <div className="space-y-3">
                    <Textarea
                        value={jsonValue}
                        onChange={(e) => {
                            setJsonValue(e.target.value);
                            setJsonError(null);
                        }}
                        rows={12}
                        className={jsonError ? 'border-destructive' : ''}
                        placeholder='{"_and": [{"status": {"_eq": "published"}}]}'
                    />
                    {jsonError && (
                        <p className="text-sm text-destructive">{jsonError}</p>
                    )}
                    <div className="flex justify-end gap-2">
                        <button
                            type="button"
                            onClick={() => {
                                setJsonValue(JSON.stringify(localPermission.permissions || {}, null, 2));
                                setJsonError(null);
                                setMode('visual');
                            }}
                            className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleJsonSave}
                            className="rounded bg-primary px-3 py-1.5 text-sm text-primary-foreground hover:bg-primary/90"
                        >
                            Apply
                        </button>
                    </div>
                </div>
            )}

            {/* Dynamic Variables Info */}
            <div className="rounded-lg border border-border bg-muted p-4">
                <h4 className="mb-2 text-sm font-medium text-foreground">
                    Dynamic Variables
                </h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                    {DYNAMIC_VARIABLES.map((v) => (
                        <div key={v.value} className="flex items-center gap-2">
                            <code className="rounded bg-background px-2 py-0.5 font-mono text-xs text-primary">
                                {v.value}
                            </code>
                            <span className="text-muted-foreground">{v.label}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
